/** biome-ignore-all lint/style/useNamingConvention: Google API payload uses snake_case */
'use client';

// Constants
const MILLISECONDS_TO_SECONDS = 1000; // Conversion factor from milliseconds to seconds

interface GoogleTokenPayload {
  iss: string;
  aud: string;
  sub: string;
  email: string;
  email_verified: boolean;
  name: string;
  picture: string;
  given_name: string;
  family_name: string;
  locale?: string;
  hd?: string; // Hosted domain for Google Workspace accounts
  exp: number;
  iat: number;
}

interface VerificationResult {
  success: boolean;
  payload?: GoogleTokenPayload;
  error?: string;
}

interface SessionResult {
  success: boolean;
  pianoUid?: string;
  pianoToken?: string;
  isNewUser?: boolean;
  message?: string;
  error?: string;
}

/**
 * Verify Google ID token using Google's tokeninfo endpoint
 * This is a simpler approach suitable for most use cases.
 * For production with high volume, consider using Google API client libraries.
 */
export async function verifyGoogleIdToken(idToken: string): Promise<VerificationResult> {
  try {
    // Verify the token using Google's tokeninfo endpoint
    const response = await fetch(`https://oauth2.googleapis.com/tokeninfo?id_token=${idToken}`, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });

    if (!response.ok) {
      return {
        error: 'Failed to verify token with Google',
        success: false,
      };
    }

    const payload = (await response.json()) as GoogleTokenPayload;

    // Verify the audience (client ID)
    // biome-ignore lint/style/noProcessEnv: NEXT_PUBLIC_GOOGLE_CLIENT_ID is a Next.js environment variable
    const expectedClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
    if (payload.aud !== expectedClientId) {
      return {
        error: 'Token audience does not match expected client ID',
        success: false,
      };
    }

    // Verify the issuer
    if (payload.iss !== 'accounts.google.com' && payload.iss !== 'https://accounts.google.com') {
      return {
        error: 'Invalid token issuer',
        success: false,
      };
    }

    // Verify the token hasn't expired
    const now = Math.floor(Date.now() / MILLISECONDS_TO_SECONDS);
    if (payload.exp < now) {
      return {
        error: 'Token has expired',
        success: false,
      };
    }

    // Token is valid
    return {
      payload,
      success: true,
    };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'Unknown error occurred',
      success: false,
    };
  }
}

/**
 * Create user session data from verified Google token
 * This calls our Piano authentication API endpoint
 */
export async function createGoogleUserSession(payload: GoogleTokenPayload): Promise<SessionResult> {
  try {
    const response = await fetch('/api/piano-auth-google', {
      body: JSON.stringify({
        email: payload.email,
        familyName: payload.family_name,
        givenName: payload.given_name,
        registrationSource: 'google_one_tap',
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
    const result = await response.json();
    if (!result.success) {
      return {
        error: result.message || 'Piano authentication failed',
        success: false,
      };
    }
    return {
      isNewUser: result.isNewUser,
      message: result.message,
      pianoToken: result.pianoToken,
      pianoUid: result.pianoUid,
      success: true,
    };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'Piano authentication failed',
      success: false,
    };
  }
}
